/// <reference lib="dom" />
//
// This one file runs in a browser, so it needs the DOM lib. The package's
// tsconfig sets `lib: ["ES2020"]` on purpose — a reporter that runs in Node has
// no business reaching for `document` — so the exception is scoped here by a
// per-file reference rather than opened up package-wide.

/**
 * The DOM walker that runs inside the page.
 *
 * Playwright's own trace snapshotter deliberately captures neither computed
 * styles nor per-element geometry — it records HTML and replays real CSS
 * instead — so it cannot answer "which element moved, and why". It is also
 * unreachable: `playwright-core`'s `exports` map does not expose the
 * snapshotter, and it operates on server-side objects a test never holds.
 * Hence this small walker of our own, which captures exactly the two things
 * that are missing.
 *
 * ⛔ `captureDomSnapshot` is handed to `page.evaluate`, which serializes it via
 * `Function.prototype.toString`. It MUST therefore be entirely self-contained:
 * no imports, no references to module scope, no closure captures. Everything it
 * needs arrives in its single argument. A stray outer reference does not fail
 * at build time — it throws inside the browser at runtime.
 */

/** A single captured element. Field names are short: this is written per node. */
export interface DomNode {
  /** Stable structural path, id-anchored. `#main>div[0]>span[2]` */
  p: string;
  /** Tag name, lowercase. */
  t: string;
  i?: string;
  c?: string;
  /** Document-space `[x, y, width, height]`, rounded. */
  r: [number, number, number, number];
  /** Direct text content, normalized and truncated. */
  x?: string;
  /** Computed styles, with inherited and default values omitted. */
  s?: Record<string, string>;
  /** Set when the element intersects a mask and is excluded from diffing. */
  m?: 1;
}

export interface DomSnapshot {
  readonly version: '1.0';
  readonly url: string;
  readonly viewport: { width: number; height: number };
  readonly content: { width: number; height: number };
  /** The document-space region the screenshot covered. */
  readonly captureBox: [number, number, number, number];
  readonly nodes: DomNode[];
  /** Elements the cap excluded. Counted, never silently dropped. */
  readonly truncatedNodes: number;
}

export interface CaptureOptions {
  readonly styleProps: readonly string[];
  /** Property → value that is so common it is not worth storing. */
  readonly defaults: Readonly<Record<string, string>>;
  /** Properties that inherit, stored only when they differ from the parent. */
  readonly inherited: readonly string[];
  readonly maxNodes: number;
  readonly maxText: number;
  /**
   * Boxes to mark as masked, in VIEWPORT coordinates — that is what
   * `locator.boundingBox()` returns on the Node side. Converted to document
   * space in here, where the scroll offset is known.
   */
  readonly maskBoxes: readonly [number, number, number, number][];
  /**
   * The region the screenshot covers, in viewport coordinates, when asserting
   * against a locator rather than the page. Null lets the walker derive it.
   */
  readonly viewportBox: [number, number, number, number] | null;
  /** True when the screenshot was taken with `fullPage`. */
  readonly fullPage: boolean;
}

/**
 * Walk the rendered DOM and return a flat, diffable snapshot.
 *
 * Flat rather than nested because the path is the match key: diffing two flat
 * arrays keyed by path is a map lookup, where diffing two trees is an edit-
 * distance problem.
 */
export function captureDomSnapshot(options: CaptureOptions): DomSnapshot {
  const { styleProps, defaults, inherited, maxNodes, maxText, maskBoxes, viewportBox, fullPage } =
    options;

  const inheritedSet: Record<string, true> = {};
  for (let i = 0; i < inherited.length; i++) inheritedSet[inherited[i] as string] = true;

  const scrollX = window.scrollX || window.pageXOffset || 0;
  const scrollY = window.scrollY || window.pageYOffset || 0;

  const nodes: DomNode[] = [];
  let truncatedNodes = 0;

  const docMasks: [number, number, number, number][] = [];
  for (let i = 0; i < maskBoxes.length; i++) {
    const mask = maskBoxes[i] as [number, number, number, number];
    docMasks.push([mask[0] + scrollX, mask[1] + scrollY, mask[2], mask[3]]);
  }

  /** Does this element's box overlap anything the user masked? */
  function isMasked(x: number, y: number, w: number, h: number): boolean {
    for (let i = 0; i < docMasks.length; i++) {
      const b = docMasks[i] as [number, number, number, number];
      if (x < b[0] + b[2] && x + w > b[0] && y < b[1] + b[3] && y + h > b[1]) return true;
    }
    return false;
  }

  /** Index among same-tag siblings — more stable than nth-child when text moves. */
  function indexAmongSiblings(el: Element): number {
    const parent = el.parentElement;
    if (!parent) return 0;
    const siblings = parent.children;
    let index = 0;
    for (let i = 0; i < siblings.length; i++) {
      const sibling = siblings[i] as Element;
      if (sibling === el) break;
      if (sibling.tagName === el.tagName) index++;
    }
    return index;
  }

  /** Direct text children only — a parent must not inherit its child's text. */
  function directText(el: Element): string {
    let text = '';
    const children = el.childNodes;
    for (let i = 0; i < children.length; i++) {
      const node = children[i] as Node;
      if (node.nodeType === 3) text += node.nodeValue ?? '';
    }
    text = text.replace(/\s+/g, ' ').trim();
    return text.length > maxText ? text.slice(0, maxText) : text;
  }

  /**
   * Store only what is surprising.
   *
   * An inherited property is recorded only where it differs from the parent, so
   * changing the body font produces one change on `body` rather than the same
   * change echoed on all six hundred of its descendants. A non-inherited
   * property is recorded only where it differs from its initial value, which is
   * what keeps a typical element down to three or four entries instead of
   * thirty.
   */
  function collectStyles(
    computed: CSSStyleDeclaration,
    parentComputed: CSSStyleDeclaration | null,
  ): Record<string, string> | undefined {
    let styles: Record<string, string> | undefined;
    for (let i = 0; i < styleProps.length; i++) {
      const prop = styleProps[i] as string;
      const value = computed.getPropertyValue(prop);
      if (!value) continue;
      if (inheritedSet[prop]) {
        if (parentComputed && parentComputed.getPropertyValue(prop) === value) continue;
      } else if (defaults[prop] === value) {
        continue;
      }
      if (!styles) styles = {};
      styles[prop] = value;
    }
    return styles;
  }

  interface Frame {
    el: Element;
    parentPath: string;
    parentComputed: CSSStyleDeclaration | null;
  }

  // Explicit stack rather than recursion: a deep page should not risk blowing
  // the call stack inside someone else's test run.
  const stack: Frame[] = [{ el: document.body, parentPath: '', parentComputed: null }];

  while (stack.length > 0) {
    const frame = stack.pop() as Frame;
    const el = frame.el;
    const computed = window.getComputedStyle(el);

    // display:none removes the whole subtree from rendering, so nothing beneath
    // it can have caused a pixel difference.
    if (computed.display === 'none') continue;

    const tag = el.tagName.toLowerCase();
    const path = el.id
      ? '#' + el.id
      : frame.parentPath
        ? frame.parentPath + '>' + tag + '[' + indexAmongSiblings(el) + ']'
        : tag;

    // Children are pushed regardless of whether this element is recorded: an
    // invisible or zero-sized wrapper routinely contains visible children.
    const children = el.children;
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({
        el: children[i] as Element,
        parentPath: path,
        parentComputed: computed,
      });
    }

    if (computed.visibility === 'hidden') continue;

    const box = el.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) continue;

    if (nodes.length >= maxNodes) {
      truncatedNodes++;
      continue;
    }

    const x = Math.round(box.left + scrollX);
    const y = Math.round(box.top + scrollY);
    const w = Math.round(box.width);
    const h = Math.round(box.height);

    const node: DomNode = { p: path, t: tag, r: [x, y, w, h] };
    if (el.id) node.i = el.id;
    const className = el.getAttribute('class');
    if (className) node.c = className.replace(/\s+/g, ' ').trim();

    const text = directText(el);
    if (text) node.x = text;

    const styles = collectStyles(computed, frame.parentComputed);
    if (styles) node.s = styles;

    if (isMasked(x, y, w, h)) node.m = 1;

    nodes.push(node);
  }

  const contentWidth = Math.max(document.documentElement.scrollWidth, window.innerWidth);
  const contentHeight = Math.max(document.documentElement.scrollHeight, window.innerHeight);

  // Which region of the document the screenshot actually shows. The report
  // positions its boxes as percentages of this, so a locator screenshot lines
  // up against the element it framed rather than against the whole viewport.
  let captureBox: [number, number, number, number];
  if (viewportBox) {
    captureBox = [
      Math.round(viewportBox[0] + scrollX),
      Math.round(viewportBox[1] + scrollY),
      Math.round(viewportBox[2]),
      Math.round(viewportBox[3]),
    ];
  } else if (fullPage) {
    captureBox = [0, 0, contentWidth, contentHeight];
  } else {
    captureBox = [Math.round(scrollX), Math.round(scrollY), window.innerWidth, window.innerHeight];
  }

  return {
    version: '1.0',
    url: location.href,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    content: { width: contentWidth, height: contentHeight },
    captureBox,
    nodes,
    truncatedNodes,
  };
}
